import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { TimelineCanvasLevel } from '../timeline/timeline-canvas/timeline-canvas-level';
import { Deferred } from '../../shared/helpers/deferred';
import { RecordData } from '../interfaces/record-data';
import { FIRST_LAYER_ID } from '../../constants';
import { HistoryData } from '../interfaces/history-data';
import { LayerData } from '../interfaces/layer-data';
import { SampleData } from '../interfaces/sample-data';
import { LayerId, Level, Milliseconds, Mp3Data } from '../../types';
import { MessageType } from '../../../../worker/src/message-type';
import { LayerLevelsRequest } from '../interfaces/layer-levels-range';
import { RecordingConfig } from '../interfaces/recording-config';
import { LayerVolume } from '../interfaces/layer-volume';
import { MessageData } from '../../../../worker/src/message-data';

let instance: RecorderStore;

@Injectable()
export class RecorderStore {
  private worker = new Worker('assets/worker.js');
  private queue: Deferred<any>[] = [];

  recordData: RecordData = {
    length: 0,
    layerIds: [
      FIRST_LAYER_ID,
    ],
  };

  historyData: HistoryData = {
    appliedTransactionsNumber: 0,
    rollbackTransactionsNumber: 0,
  };

  onRecordChange: EventEmitter<RecordData> = new EventEmitter();
  onLayerChange: EventEmitter<LayerData> = new EventEmitter();
  onSampleFinish: EventEmitter<SampleData> = new EventEmitter();
  onHistoryChange: EventEmitter<HistoryData> = new EventEmitter();

  constructor(zone: NgZone) {
    if (instance) {
      return instance;
    } else {
      instance = this;
    }

    this.worker.addEventListener('message', event => zone.run(() => {
      this.onMessage(event);
    }));

    this.onRecordChange.subscribe(recordData => this.recordData = recordData);
    this.onHistoryChange.subscribe(historyData => this.historyData = historyData);
  }

  get lastLayerId(): LayerId {
    const layerIds = this.recordData.layerIds;

    return layerIds[layerIds.length - 1];
  }

  undo() {
    return this.postMessage(MessageType.UNDO);
  }

  redo() {
    return this.postMessage(MessageType.REDO);
  }

  getLevels(payload: LayerLevelsRequest): Promise<Level[]> {
    return this.postPromiseMessage(MessageType.GET_LEVELS, payload);
  }

  getRecordLength(): Promise<Milliseconds> {
    return this.postPromiseMessage(MessageType.GET_RECORD_LENGTH);
  }

  addRecordingProgress(float32Array: Float32Array): Promise<TimelineCanvasLevel[]> {
    return this.postPromiseMessage(MessageType.ADD_RECORDING_PROGRESS, float32Array);
  }

  startRecording(payload: RecordingConfig) {
    this.postMessage(MessageType.START_RECORDING, payload);
  }

  addLayer(): Promise<LayerId[]> {
    return this.postPromiseMessage(MessageType.ADD_LAYER);
  }

  removeLayer(layerId: LayerId): Promise<LayerId[]> {
    return this.postPromiseMessage(MessageType.REMOVE_LAYER, layerId);
  }

  stopRecording(): Promise<SampleData> {
    return this.postPromiseEventMessage(MessageType.STOP_RECORDING, 'onSampleFinish');
  }

  postPromiseEventMessage(messageType: MessageType, eventName: string, payload?: any): Promise<any> {
    const promise = this.postPromiseMessage(messageType, payload);
    promise.then(data => this[eventName].emit(data));
    return promise;
  }

  getRecordData(startPosition: Milliseconds): Promise<Float32Array> {
    return this.postPromiseMessage(MessageType.GET_RECORD_DATA, startPosition);
  }

  getCompiledRecord(): Promise<Mp3Data> {
    return this.postPromiseMessage(MessageType.GET_COMPILED_RECORD);
  }

  setLayerVolume(payload: LayerVolume) {
    this.postMessage(MessageType.SET_LAYER_VOLUME, payload);
  }

  private postPromiseMessage(type: MessageType, payload?: any): Promise<any> {
    const deferred = new Deferred();

    this.queue.push(deferred);
    this.postMessage(type, payload);

    return deferred.promise;
  }

  private postMessage(type: MessageType, payload?: any) {
    this.worker.postMessage({type, payload} as MessageData);
  }

  private onMessage(event: MessageEvent) {
    if (!event.data) {
      return;
    }

    const data: MessageData = event.data;

    switch (data.type) {
      case MessageType.ON_RECORD_CHANGE:
        this.onRecordChange.emit(data.payload as RecordData);
        break;
      case MessageType.ON_LAYER_CHANGE:
        this.onLayerChange.emit(data.payload as LayerData);
        break;
      case MessageType.ON_HISTORY_CHANGE:
        this.onHistoryChange.emit(data.payload as HistoryData);
        break;
      default:
        this.queue.shift()
          .resolve(data.payload);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/rec/stores/recorder.store.ts